import { useEffect, useState, type FormEvent } from 'react';
import { Seitenkopf } from '../components/Seitenkopf';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { IconPlus } from '../components/icons';
import { DEMO, api, ApiFehler } from '../lib/api';
import { formatDatum } from '../lib/format';

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';

interface Kennzahlen {
  mandanten: number;
  nutzer: number;
  objekte: number;
  einheiten: number;
  mietverhaeltnisse: number;
  abrechnungen: number;
  partner: number;
}
interface Mandant {
  id: string;
  name: string;
  typ: string;
  stufe: number;
  partner: string | null;
  nutzer: number;
  objekte: number;
  einheiten: number;
  angelegtAm: string | null;
}
interface Partner {
  id: string;
  name: string;
  email: string | null;
  telefon: string | null;
  provisionProzent: number;
  aktiv: boolean;
  geworbeneKunden: number;
}
interface SupportAnfrage {
  id: string;
  mandant: string | null;
  email: string | null;
  betreff: string;
  nachricht: string;
  status: 'neu' | 'gelesen' | 'erledigt';
  erstelltAm: string | null;
}

const DEMO_KENN: Kennzahlen = {
  mandanten: 14,
  nutzer: 21,
  objekte: 39,
  einheiten: 212,
  mietverhaeltnisse: 178,
  abrechnungen: 26,
  partner: 3,
};
const DEMO_MANDANTEN: Mandant[] = [
  { id: 'm1', name: 'Hausverwaltung Berg', typ: 'hausverwaltung', stufe: 3, partner: 'Partner Nord', nutzer: 3, objekte: 12, einheiten: 84, angelegtAm: '2026-07-02' },
  { id: 'm2', name: 'Privatvermieter Eser', typ: 'privatvermieter', stufe: 1, partner: null, nutzer: 1, objekte: 1, einheiten: 4, angelegtAm: '2026-08-19' },
  { id: 'm3', name: 'WEG Lindenhof', typ: 'weg', stufe: 4, partner: 'Partner Süd', nutzer: 2, objekte: 3, einheiten: 46, angelegtAm: '2026-09-01' },
];
const DEMO_PARTNER: Partner[] = [
  { id: 'p1', name: 'Partner Nord', email: null, telefon: null, provisionProzent: 15, aktiv: true, geworbeneKunden: 6 },
  { id: 'p2', name: 'Partner Süd', email: null, telefon: null, provisionProzent: 12.5, aktiv: true, geworbeneKunden: 4 },
];

export function Admin() {
  const [kennzahlen, setKennzahlen] = useState<Kennzahlen | null>(DEMO ? DEMO_KENN : null);
  const [mandanten, setMandanten] = useState<Mandant[]>(DEMO ? DEMO_MANDANTEN : []);
  const [partner, setPartner] = useState<Partner[]>(DEMO ? DEMO_PARTNER : []);
  const [anfragen, setAnfragen] = useState<SupportAnfrage[]>(
    DEMO
      ? [
          { id: 's1', mandant: 'Privatvermieter Eser', email: null, betreff: 'Wärmemengenzähler erfassen', nachricht: 'Wie trage ich den Anfangs- und Endstand des WMZ ein?', status: 'neu', erstelltAm: '2026-09-14' },
          { id: 's2', mandant: 'Hausverwaltung Berg', email: null, betreff: 'CSV-Import der Belege', nachricht: 'Gibt es eine Importvorlage für Belege?', status: 'gelesen', erstelltAm: '2026-09-10' },
        ]
      : [],
  );
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);
  const [formOffen, setFormOffen] = useState(false);

  useEffect(() => {
    if (DEMO) return;
    setLaden(true);
    Promise.all([
      api.get<{ kennzahlen: Kennzahlen; mandanten: Mandant[] }>('/admin/uebersicht'),
      api.get<{ vertriebspartner: Partner[] }>('/admin/vertriebspartner'),
      api.get<{ anfragen: SupportAnfrage[] }>('/admin/support'),
    ])
      .then(([u, p, s]) => {
        setKennzahlen(u.kennzahlen);
        setMandanten(u.mandanten);
        setPartner(p.vertriebspartner);
        setAnfragen(s.anfragen);
        setFehler(null);
      })
      .catch((e) => setFehler(e instanceof ApiFehler ? e.message : 'Das Admin-Dashboard konnte nicht geladen werden.'))
      .finally(() => setLaden(false));
  }, []);

  function anfrageStatus(id: string, status: SupportAnfrage['status']) {
    setAnfragen((xs) => xs.map((a) => (a.id === id ? { ...a, status } : a)));
    if (!DEMO) api.patch(`/admin/support/${id}`, { status }).catch(() => {});
  }

  return (
    <div>
      <Seitenkopf
        titel="Administration"
        beschreibung="Plattformübersicht für den Betreiber: Mandanten, Nutzung und Vertriebspartner. Kein Zugriff auf Mieter- oder Belegdaten der Mandanten — ausschließlich Metadaten."
      />

      {fehler && (
        <Card className="mb-4">
          <p className="text-sm text-danger">{fehler}</p>
        </Card>
      )}

      {laden && !kennzahlen ? (
        <p className="text-sm text-fg-muted">Wird geladen …</p>
      ) : (
        <div className="space-y-6">
          {kennzahlen && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
              <Kachel label="Mandanten" wert={kennzahlen.mandanten} />
              <Kachel label="Nutzer" wert={kennzahlen.nutzer} />
              <Kachel label="Objekte" wert={kennzahlen.objekte} />
              <Kachel label="Einheiten" wert={kennzahlen.einheiten} />
              <Kachel label="Mietverhältnisse" wert={kennzahlen.mietverhaeltnisse} />
              <Kachel label="Abrechnungen" wert={kennzahlen.abrechnungen} />
              <Kachel label="Partner" wert={kennzahlen.partner} betont />
            </div>
          )}

          {/* Support-Anfragen (Glocke) */}
          <section>
            <h2 className="mb-2 px-1 text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              Support-Anfragen{' '}
              {anfragen.filter((a) => a.status === 'neu').length > 0 && (
                <Badge ton="warnung">{anfragen.filter((a) => a.status === 'neu').length} neu</Badge>
              )}
            </h2>
            {anfragen.length === 0 ? (
              <p className="px-1 text-sm text-fg-muted">Keine Support-Anfragen.</p>
            ) : (
              <div className="space-y-2.5">
                {anfragen.map((a) => (
                  <Card key={a.id}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-fg">{a.betreff}</span>
                          <Badge ton={a.status === 'neu' ? 'warnung' : a.status === 'gelesen' ? 'info' : 'erfolg'}>
                            {a.status === 'neu' ? 'Neu' : a.status === 'gelesen' ? 'Gelesen' : 'Erledigt'}
                          </Badge>
                        </div>
                        <p className="mt-1 text-sm text-fg-muted">{a.nachricht}</p>
                        <p className="mt-1 text-xs text-fg-subtle">
                          {[a.mandant, a.email].filter(Boolean).join(' · ')}
                          {a.erstelltAm ? ` · ${formatDatum(a.erstelltAm)}` : ''}
                        </p>
                      </div>
                      <div className="flex gap-1.5">
                        {a.status !== 'gelesen' && a.status !== 'erledigt' && (
                          <Button variante="dezent" groesse="sm" onClick={() => anfrageStatus(a.id, 'gelesen')}>
                            Gelesen
                          </Button>
                        )}
                        {a.status !== 'erledigt' && (
                          <Button variante="sekundaer" groesse="sm" onClick={() => anfrageStatus(a.id, 'erledigt')}>
                            Erledigt
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </section>

          {/* Vertriebspartner */}
          <section>
            <div className="mb-2 flex items-center justify-between px-1">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">Vertriebspartner</h2>
              <Button icon={<IconPlus groesse={16} />} groesse="sm" onClick={() => setFormOffen((o) => !o)}>
                Partner
              </Button>
            </div>
            {formOffen && (
              <PartnerFormular
                onAngelegt={(p) => {
                  setPartner((xs) => [...xs, p]);
                  setFormOffen(false);
                }}
                onAbbruch={() => setFormOffen(false)}
              />
            )}
            <div className="space-y-2.5">
              {partner.length === 0 ? (
                <p className="px-1 text-sm text-fg-muted">Noch kein Vertriebspartner angelegt.</p>
              ) : (
                partner.map((p) => (
                  <Card key={p.id}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-fg">{p.name}</span>
                          {p.aktiv ? <Badge ton="erfolg">aktiv</Badge> : <Badge ton="neutral">inaktiv</Badge>}
                          <Badge ton="info">{p.provisionProzent.toLocaleString('de-DE')} % Provision</Badge>
                        </div>
                        {(p.email || p.telefon) && (
                          <p className="mt-1 text-xs text-fg-subtle">{[p.email, p.telefon].filter(Boolean).join(' · ')}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xs uppercase tracking-wide text-fg-subtle">Geworbene Kunden</p>
                        <p className="tabellenziffern text-lg font-semibold text-fg">{p.geworbeneKunden}</p>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </section>

          {/* Mandanten */}
          <section>
            <h2 className="mb-2 px-1 text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              Mandanten ({mandanten.length})
            </h2>
            <Card ohnePolster>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-fg-subtle">
                      <th className="px-4 py-2 font-medium">Mandant</th>
                      <th className="px-4 py-2 font-medium">Stufe</th>
                      <th className="px-4 py-2 text-right font-medium">Objekte</th>
                      <th className="px-4 py-2 text-right font-medium">Einheiten</th>
                      <th className="px-4 py-2 text-right font-medium">Nutzer</th>
                      <th className="px-4 py-2 font-medium">Partner</th>
                      <th className="px-4 py-2 font-medium">Angelegt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mandanten.map((m) => (
                      <tr key={m.id} className="border-b border-border last:border-0">
                        <td className="px-4 py-2 text-fg">
                          {m.name}
                          <span className="ml-1 text-xs text-fg-subtle">· {m.typ}</span>
                        </td>
                        <td className="px-4 py-2 text-fg-muted">{m.stufe}</td>
                        <td className="px-4 py-2 text-right tabellenziffern text-fg">{m.objekte}</td>
                        <td className="px-4 py-2 text-right tabellenziffern text-fg">{m.einheiten}</td>
                        <td className="px-4 py-2 text-right tabellenziffern text-fg">{m.nutzer}</td>
                        <td className="px-4 py-2 text-fg-muted">{m.partner ?? '—'}</td>
                        <td className="px-4 py-2 text-fg-subtle">{m.angelegtAm ? formatDatum(m.angelegtAm) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>
        </div>
      )}
    </div>
  );
}

function Kachel({ label, wert, betont }: { label: string; wert: number; betont?: boolean }) {
  return (
    <div className={`rounded-lg border p-3 ${betont ? 'border-accent/40 bg-accent-soft' : 'border-border bg-surface-2'}`}>
      <p className="text-xs text-fg-muted">{label}</p>
      <p className={`mt-1 tabellenziffern text-xl font-bold ${betont ? 'text-accent-soft-fg' : 'text-fg'}`}>{wert}</p>
    </div>
  );
}

function PartnerFormular({
  onAngelegt,
  onAbbruch,
}: {
  onAngelegt: (p: Partner) => void;
  onAbbruch: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [telefon, setTelefon] = useState('');
  const [provision, setProvision] = useState('10');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!name.trim()) {
      setFehler('Bitte einen Namen angeben.');
      return;
    }
    const prov = Number(provision.replace(',', '.')) || 0;
    setSendet(true);
    try {
      let id = `neu-${Date.now()}`;
      if (!DEMO) {
        const r = await api.post<{ id: string }>('/admin/vertriebspartner', {
          name: name.trim(),
          email: email.trim() || undefined,
          telefon: telefon.trim() || undefined,
          provisionProzent: prov,
        });
        id = r.id;
      }
      onAngelegt({
        id,
        name: name.trim(),
        email: email.trim() || null,
        telefon: telefon.trim() || null,
        provisionProzent: prov,
        aktiv: true,
        geworbeneKunden: 0,
      });
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Der Partner konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card titel="Neuer Vertriebspartner" className="mb-3">
      <form onSubmit={speichern} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input label="Provision (% p. a.)" inputMode="decimal" value={provision} onChange={(e) => setProvision(e.target.value)} />
          <Input label="E-Mail (optional)" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input label="Telefon (optional)" value={telefon} onChange={(e) => setTelefon(e.target.value)} />
        </div>
        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}
        <div className="flex gap-2">
          <Button type="submit" disabled={sendet}>
            {sendet ? 'Wird gespeichert …' : 'Partner anlegen'}
          </Button>
          <Button type="button" variante="dezent" onClick={onAbbruch}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}

export default Admin;
